import type { AssignmentSource } from "@hightouch/lib/llm/lifecycle-studio/dynamic-content/types";
import { NATIVE_DELIVERY_TARGET } from "@hightouch/lib/llm/lifecycle-studio/esp-templating/dialects";

/**
 * Campaign / brand / workspace row for the assignment inherit walk
 * `resolveDefaultAssignmentSource` does on the backend. NULL assignment means
 * inherit; an explicit ESP selection at a scope (`native_delivery` or a
 * destination id) closes the walk the same way it closes the ESP walk.
 */
export type AssignmentInheritScope = {
  assignmentSource?: string | null;
  destinationId?: string | number | null;
  targetKind?: string | null;
};

export function parseAssignmentSource(value: unknown): AssignmentSource | null {
  if (value === "audience_schema" || value === "esp_profile") return value;
  return null;
}

/** Pure derive used when the inherit chain has no explicit value. */
export function assignmentSourceFromResolvedEsp(args: {
  isNativeDelivery: boolean;
}): AssignmentSource {
  return args.isNativeDelivery ? "audience_schema" : "esp_profile";
}

function hasNamedDestination(
  destinationId: string | number | null | undefined,
): boolean {
  return destinationId != null && String(destinationId) !== "";
}

/** True when this scope named an ESP (ND or destination) and stops the ESP walk. */
function hasExplicitEspSelection(scope: AssignmentInheritScope): boolean {
  return (
    scope.targetKind === NATIVE_DELIVERY_TARGET ||
    hasNamedDestination(scope.destinationId)
  );
}

function resolveAssignmentSourceChain(args: {
  campaign?: AssignmentInheritScope | null;
  brand?: AssignmentInheritScope | null;
  workspaceAssignmentSource?: unknown;
  isNativeDelivery: boolean;
}): AssignmentSource {
  const fromCampaign = parseAssignmentSource(args.campaign?.assignmentSource);
  if (fromCampaign) return fromCampaign;
  if (args.campaign && hasExplicitEspSelection(args.campaign)) {
    return assignmentSourceFromResolvedEsp(args);
  }

  const fromBrand = parseAssignmentSource(args.brand?.assignmentSource);
  if (fromBrand) return fromBrand;
  if (args.brand && hasExplicitEspSelection(args.brand)) {
    return assignmentSourceFromResolvedEsp(args);
  }

  const fromWorkspace = parseAssignmentSource(args.workspaceAssignmentSource);
  if (fromWorkspace) return fromWorkspace;

  return assignmentSourceFromResolvedEsp(args);
}

/**
 * Resolve library `assignment_source` from campaign → brand → workspace, then
 * from the resolved ESP when every scope is unset. Always returns a concrete
 * value — never null. Email stamps are the caller's job, matching
 * `resolveDefaultAssignmentSource` preferring `parseCompileTargetFromMessage`.
 */
export function resolveInheritedAssignmentSource(args: {
  campaign?: AssignmentInheritScope | null;
  brand?: AssignmentInheritScope | null;
  workspaceAssignmentSource?: unknown;
  isNativeDelivery: boolean;
}): AssignmentSource {
  const resolved = resolveAssignmentSourceChain(args);

  // Native Delivery cannot honor ESP-profile predicates. Prefer warehouse
  // audiences even when a leftover scope default says esp_profile.
  if (resolved === "esp_profile" && args.isNativeDelivery) {
    return "audience_schema";
  }

  return resolved;
}
